// ./src/utils/api/EventTicketService.ts
//
// EventTicket (an ISSUED ticket — the printable credential with its own code)
// service. OpResult-native, event-scoped. Distinct from TicketType (the product)
// and EventGuest (the attendee). This is what gets sold at the door / in person,
// pre-generated as inventory, later assigned to a guest, and printed. See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { SCAN_ANSWER_FIELDS, type AdmissionMove } from "./EventGuestService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { BibItemInput, BibSortOrder } from "./bibConstants";
import type { ServicePointRef } from "./ServicePointService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants — mirror the server boundary (src/boundary/ticket.ts)
// =============================================================================

/** Lifecycle of an issued ticket. */
export const TICKET_STATE = {
  GENERATED: "generated",
  // ⚠ Was MISSING from this mirror until 2026-09-09, so `ticketStateLabel` rendered a held
  // ticket as the raw string "reserved". A #40 rebill ticket sits in this state for the whole
  // life of its pay link, which is exactly when door staff are most likely to look at one.
  RESERVED: "reserved",
  SOLD: "sold",
  ASSIGNED: "assigned",
  CHECKED_IN: "checked_in",
  VOID: "void",
  REFUNDED: "refunded",
} as const;

export const TICKET_STATE_VALUES = Object.values(TICKET_STATE);

export const TICKET_STATE_LABELS: Record<string, string> = {
  [TICKET_STATE.GENERATED]: "Generated",
  [TICKET_STATE.RESERVED]: "Held, not paid for",
  [TICKET_STATE.SOLD]: "Sold",
  [TICKET_STATE.ASSIGNED]: "Assigned",
  [TICKET_STATE.CHECKED_IN]: "Checked in",
  [TICKET_STATE.VOID]: "Void",
  [TICKET_STATE.REFUNDED]: "Refunded",
};

/** Mantine color per lifecycle state (for badges). */
export const TICKET_STATE_COLORS: Record<string, string> = {
  [TICKET_STATE.GENERATED]: "gray",
  [TICKET_STATE.RESERVED]: "yellow",
  [TICKET_STATE.SOLD]: "blue",
  [TICKET_STATE.ASSIGNED]: "teal",
  [TICKET_STATE.CHECKED_IN]: "green",
  [TICKET_STATE.VOID]: "red",
  [TICKET_STATE.REFUNDED]: "orange",
};

/**
 * Ticket states that OCCUPY a place — mirrors the server's TICKET_STATE_OCCUPYING
 * (src/boundary/ticket.ts). ⚠ Includes `reserved`: a held-but-unpaid ticket still occupies the
 * seat, and it is what tells the check-in console that a guest must be admitted through their
 * TICKET rather than their guest code.
 */
export const TICKET_STATE_OCCUPYING: string[] = [
  TICKET_STATE.RESERVED,
  TICKET_STATE.SOLD,
  TICKET_STATE.ASSIGNED,
  TICKET_STATE.CHECKED_IN,
];

export const ticketStateLabel = (state?: string | null): string =>
  (state && TICKET_STATE_LABELS[state]) || state || "—";

export const ticketStateColor = (state?: string | null): string =>
  (state && TICKET_STATE_COLORS[state]) || "gray";

/** Where/how an issued ticket was sold. */
export const TICKET_SALE_CHANNEL = {
  DOOR: "door",
  ONLINE: "online",
  IN_PERSON: "in_person",
  COMP: "comp",
  OTHER: "other",
} as const;

export const TICKET_SALE_CHANNEL_VALUES = Object.values(TICKET_SALE_CHANNEL);

export const TICKET_SALE_CHANNEL_LABELS: Record<string, string> = {
  [TICKET_SALE_CHANNEL.DOOR]: "At the door",
  [TICKET_SALE_CHANNEL.ONLINE]: "Online",
  [TICKET_SALE_CHANNEL.IN_PERSON]: "In person",
  [TICKET_SALE_CHANNEL.COMP]: "Complimentary",
  [TICKET_SALE_CHANNEL.OTHER]: "Other",
};

export const ticketSaleChannelLabel = (channel?: string | null): string =>
  (channel && TICKET_SALE_CHANNEL_LABELS[channel]) || channel || "—";

/** Door-scan actions (mirror the server boundary). */
export const TICKET_SCAN_ACTION = {
  CHECK_IN: "check_in",
  CHECK_OUT: "check_out",
  UNDO_CHECK_IN: "undo_check_in",
  UNDO_CHECK_OUT: "undo_check_out",
} as const;

// =============================================================================
// GraphQL
// =============================================================================

export const EVENT_TICKET_FIELDS = `
  id
  accountId
  eventId
  eventTimeSlotId
  ticketTypeId
  eventGuestId
  personId
  paymentId
  saleGroupId
  ticketOrderId
  ticketCode
  ticketState
  isTest
  saleChannel
  price
  currencyCode
  holderName
  holderEmail
  holderPhone
  soldAt
  soldByUserId
  checkedInAt
  checkedInByUserId
  checkedInByLabel
  checkedOutAt
  checkedOutByUserId
  checkedOutByLabel
  checkedInServicePoint { id name kind removedAt }
  checkedOutServicePoint { id name kind removedAt }
  isAddOn
  checkedInDateOverride
  voidedAt
  seatAssignment
  bibNumber
  note
  status
  rights
  createdAt
  updatedAt
  ticketOrder { id personId buyerName buyerEmail buyerPhone orderStatus placedAt totalAmount currencyCode }
  holderRsvpStatus
  ticketType { id name }
`;

const EVENT_TICKET_LIST_QUERY = gql`
  query EventTicketList($filter: EventTicketFilter, $params: PaginationAndSorting) {
    eventTicketList(filter: $filter, params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_GET_QUERY = gql`
  query EventTicketGet($id: ID) {
    eventTicketGet(id: $id) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_CREATE_MUTATION = gql`
  mutation EventTicketCreate($params: EventTicketInput) {
    eventTicketCreate(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_UPDATE_MUTATION = gql`
  mutation EventTicketUpdate($where: EventTicketWhereInput, $params: EventTicketInput) {
    eventTicketUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_REMOVE_MUTATION = gql`
  mutation EventTicketRemove($where: EventTicketWhereInput) {
    eventTicketRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_SCAN_MUTATION = gql`
  mutation EventTicketScan($params: EventTicketScanInput) {
    eventTicketScan(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} ${SCAN_ANSWER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_REASSIGN_MUTATION = gql`
  mutation EventTicketReassign($params: EventTicketReassignInput) {
    eventTicketReassign(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_CHANGE_TYPE_MUTATION = gql`
  mutation EventTicketChangeType($params: EventTicketChangeTypeInput) {
    eventTicketChangeType(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_REFUND_MUTATION = gql`
  mutation EventTicketRefund($params: EventTicketRefundInput) {
    eventTicketRefund(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// Order-aware refund (Stripe-executing): a single ticket or the whole order, partial/full,
// keep-alive, bypass. Rolls the payment ledger + order status.
const TICKET_ORDER_REFUND_MUTATION = gql`
  mutation TicketOrderRefund($params: TicketOrderRefundInput) {
    ticketOrderRefund(params: $params) {
      code
      data { orderId scope refundedAmount keptAlive bypassed processor processorRefundId paymentStatus voidedTicketIds }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #187 — what removing tickets (the Box Office) or guests (the guest list) would do: the places back, the orders
// refunded WHOLE, the money, and whether the removal must say refund or keep. Read when the confirm opens.
const TICKETS_REMOVAL_PLAN_QUERY = gql`
  query TicketsRemovalPlan($params: TicketsRemovalInput) {
    ticketsRemovalPlan(params: $params) {
      code
      data {
        ticketCount
        placeCount
        refundableAmount
        currencyCode
        needsRefundChoice
        mayDecideRefund
        blockedReason
        blockedMessage
        holderGuestCount
        otherHolderNames
        orders { orderId wholeOrder ticketCount refundableAmount currencyCode processor isTest buyerName referenceCode }
      }
      errors { name errors warnings }
    }
  }
`;

// …and the removal itself: refund first (or keep, as chosen), then void and remove. A long selection is done over
// several calls — `remainingOrders` says what is left; call again with the same input.
const TICKETS_REMOVE_MUTATION = gql`
  mutation TicketsRemove($params: TicketsRemovalInput) {
    ticketsRemove(params: $params) {
      code
      data { removedTicketIds removedGuestIds refundedOrderIds refundedAmount currencyCode remainingOrders }
      errors { name errors warnings }
    }
  }
`;

// …and the bulk REFUND (no removal): the selection refunded per ORDER — the whole order when all of it is selected (its fees
// too), else those tickets; one refund per order. A few orders per call — `remainingOrders`; call again until 0.
const TICKETS_REFUND_MUTATION = gql`
  mutation TicketsRefund($params: TicketsRefundInput) {
    ticketsRefund(params: $params) {
      code
      data { refundedTicketIds refundedOrderIds refundedAmount currencyCode remainingOrders }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #171 — the door gives an order's tickets their BIB numbers in one save (one per ticket; all or nothing).
const EVENT_TICKET_SET_BIBS_MUTATION = gql`
  mutation EventTicketSetBibs($params: EventTicketSetBibsInput) {
    eventTicketSetBibs(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_ASSIGN_BIBS_MUTATION = gql`
  mutation EventTicketAssignBibs($params: EventTicketAssignBibsInput) {
    eventTicketAssignBibs(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_TICKET_GENERATE_MUTATION = gql`
  mutation EventTicketGenerate($params: EventTicketGenerateInput) {
    eventTicketGenerate(params: $params) {
      code
      data { ${EVENT_TICKET_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface EventTicket {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  /** What was sold (→ ticket_type); null = generic inventory. */
  ticketTypeId?: string | null;
  /** Assigned holder (→ event_guest); null = unassigned inventory. */
  eventGuestId?: string | null;
  /** Address-book buyer/holder (→ person). */
  personId?: string | null;
  /** The money row (→ payment). */
  paymentId?: string | null;
  /** Groups tickets sold together in one sale. */
  saleGroupId?: string | null;
  /** The order this ticket belongs to (→ ticket_order) — for order-level refunds. */
  ticketOrderId?: string | null;
  /** The printed/scanned code (unique per event). */
  ticketCode?: string | null;
  /** generated | sold | assigned | checked_in | void | refunded. */
  ticketState?: string | null;
  /** ROADMAP #188 — sold by a test run (the event was in test mode); hidden once the event is live. */
  isTest?: boolean | null;
  /** door | online | in_person | comp | other. */
  saleChannel?: string | null;
  /** Price paid (decimal string, snapshot at sale). */
  price?: string | null;
  currencyCode?: string | null;
  holderName?: string | null;
  holderEmail?: string | null;
  holderPhone?: string | null;
  soldAt?: string | null;
  soldByUserId?: string | null;
  checkedInAt?: string | null;
  checkedInByUserId?: string | null;
  checkedInByLabel?: string | null;
  checkedOutAt?: string | null;
  checkedOutByUserId?: string | null;
  checkedOutByLabel?: string | null;
  /** #169 — where it was scanned in (an add-on: where it was HANDED OUT) / out; null = not recorded. */
  checkedInServicePoint?: ServicePointRef | null;
  checkedOutServicePoint?: ServicePointRef | null;
  /** #169 — an add-on (a T-shirt, parking), not an admission: its "check-in" is its hand-out. */
  isAddOn?: boolean | null;
  /** ROADMAP A4: crew checked it in on a date it is not for. */
  checkedInDateOverride?: boolean | null;
  /** #228 — set ONLY on eventTicketScan's answer: the ticket's door trail, oldest first. */
  admissionTrail?: AdmissionMove[] | null;
  /** #228 — set ONLY on eventTicketScan's answer: what it did (SCAN_OUTCOME in utils/servicePoints). */
  scanOutcome?: string | null;
  voidedAt?: string | null;
  seatAssignment?: string | null;
  /** Race bib number assigned to this ticket (sport events). */
  bibNumber?: string | null;
  note?: string | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  /** The order this ticket belongs to (buyer + sale info). */
  /**
   * #37 phase 2 — the HOLDER's rsvp_status, so the door can see that a live ticket belongs to
   * someone still awaiting review ('pending') or refused ('denied'). Null on a normal sale.
   */
  holderRsvpStatus?: string | null;
  ticketOrder?: TicketOrderRef | null;
  /** The product sold (General Admission, VIP, …). */
  ticketType?: TicketTypeRef | null;
}

/** Minimal buyer/order info nested on a ticket (from ticket_order). */
export interface TicketOrderRef {
  id?: string;
  /** The buyer's address-book person — lets the UI open the purchaser's contact (Stage 7 Part B). */
  personId?: string | null;
  buyerName?: string | null;
  buyerEmail?: string | null;
  buyerPhone?: string | null;
  orderStatus?: string | null;
  /** When the order was placed — #37 phase 2 uses it to age the card authorization. */
  placedAt?: string | null;
  totalAmount?: string | null;
  currencyCode?: string | null;
}

/** Minimal ticket-type info nested on a ticket. */
export interface TicketTypeRef {
  id?: string;
  name?: string | null;
}

export interface EventTicketInput {
  eventId?: string | null;
  ticketCode?: string | null;
  eventTimeSlotId?: string | null;
  ticketTypeId?: string | null;
  eventGuestId?: string | null;
  personId?: string | null;
  paymentId?: string | null;
  saleGroupId?: string | null;
  ticketState?: string | null;
  saleChannel?: string | null;
  price?: string | null;
  currencyCode?: string | null;
  holderName?: string | null;
  holderEmail?: string | null;
  holderPhone?: string | null;
  seatAssignment?: string | null;
  bibNumber?: string | null;
  soldAt?: string | null;
  note?: string | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change ticketState / saleChannel / status
// (server-enforced).
export interface EventTicketBulkInput {
  ticketState?: string | null;
  saleChannel?: string | null;
  status?: number | null;
}

export interface EventTicketFilter {
  id?: string[] | null;
  /** #169 — scanned in (an add-on: handed out) at one of these service points. */
  checkedInServicePointId?: string[] | null;
  eventId?: string[] | null;
  eventTimeSlotId?: string[] | null;
  ticketTypeId?: string[] | null;
  eventGuestId?: string[] | null;
  personId?: string[] | null;
  saleGroupId?: string[] | null;
  /** The order these tickets belong to — used to load an order's sibling tickets at check-in. */
  ticketOrderId?: string[] | null;
  ticketState?: string[] | null;
  saleChannel?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface EventTicketWhereInput {
  id?: string | string[];
}

/** A readable label for who holds a ticket (holder snapshot; falls back to code). */
export const eventTicketHolderName = (t: EventTicket): string => {
  if (t.holderName && t.holderName.trim()) {
    return t.holderName.trim();
  }

  return t.ticketCode || "—";
};

// =============================================================================
// Service
// =============================================================================

export class EventTicketService {
  static async list(
    filter?: EventTicketFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_TICKET_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: EventTicketFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<EventTicket>(EventTicketService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_TICKET_GET_QUERY, { id });
  }

  static async create(params: EventTicketInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_CREATE_MUTATION, { params });
  }

  static async update(where: EventTicketWhereInput, params: EventTicketInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_REMOVE_MUTATION, { where: { id } });
  }

  static async bulkUpdate(ids: string[], params: EventTicketBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_TICKET_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_TICKET_REMOVE_MUTATION, { where: { id: ids } });
  }

  /**
   * Door scan. ROADMAP A4: on a recurring event a ticket for another date is refused (field `overrideDate`);
   * pass `overrideDate: true` to admit anyway — the server records the override.
   */
  static async scan(id: string, action: string, opts: { overrideDate?: boolean; servicePointId?: string | null } = {}): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_SCAN_MUTATION, {
      params: {
        id,
        action,
        ...(opts.overrideDate === true ? { overrideDate: true } : {}),
        // #169 — the point the scanner stands at (for an add-on, where it is handed out).
        ...(opts.servicePointId ? { servicePointId: opts.servicePointId } : {}),
      },
    });
  }

  static async reassign(id: string, guest: TicketReassignGuestInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_REASSIGN_MUTATION, { params: { id, guest } });
  }

  static async changeType(id: string, ticketTypeId: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_CHANGE_TYPE_MUTATION, { params: { id, ticketTypeId } });
  }

  static async refund(id: string, params: TicketRefundInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_REFUND_MUTATION, { params: { id, ...params } });
  }

  /** Order-aware refund: one ticket or the whole order, executed on Stripe (unless bypassed). */
  static async refundOrder(params: TicketOrderRefundInput): Promise<OpResult> {
    return ApiService.executeMutation(TICKET_ORDER_REFUND_MUTATION, { params });
  }

  /** #187 — what removing these tickets / guests would do (read fresh each time the confirm opens). */
  static async removalPlan(params: TicketsRemovalInput): Promise<OpResult> {
    return ApiService.executeQuery(TICKETS_REMOVAL_PLAN_QUERY, { params }, { fetchPolicy: "network-only" });
  }

  /** #187 — remove tickets and/or guests, settling the money as `params.refund` says (one call; see `remainingOrders`). */
  static async removeTickets(params: TicketsRemovalInput): Promise<OpResult> {
    return ApiService.executeMutation(TICKETS_REMOVE_MUTATION, { params });
  }

  /** #187 — refund the selected tickets per order (one call; see `remainingOrders`). */
  static async refundTickets(params: TicketsRefundInput): Promise<OpResult> {
    return ApiService.executeMutation(TICKETS_REFUND_MUTATION, { params });
  }

  static async generate(params: TicketGenerateInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_GENERATE_MUTATION, { params });
  }

  /** Assign bib numbers to the event's confirmed tickets, per each type's config. */
  static async assignBibs(params: TicketAssignBibsInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_ASSIGN_BIBS_MUTATION, { params });
  }

  /**
   * ROADMAP #171 — set these tickets' BIB numbers in one save (the door's party drawer; one per ticket). All or nothing:
   * a number another ticket holds is refused, naming them, keyed `items[<index>].bibNumber`. Owner or check-in crew.
   */
  static async setBibs(eventId: string, items: BibItemInput[]): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TICKET_SET_BIBS_MUTATION, { params: { eventId, items } });
  }
}

export interface TicketReassignGuestInput {
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface TicketRefundInput {
  amount?: string | null;
  keepTicket?: boolean;
  bypassProcessor?: boolean;
  note?: string | null;
}

export interface TicketOrderRefundInput {
  orderId: string;
  /** 'ticket' (needs ticketId) or 'order' (all live tickets). */
  scope?: 'ticket' | 'order';
  ticketId?: string | null;
  amount?: number | null;
  keepTickets?: boolean;
  bypassProcessor?: boolean;
  note?: string | null;
}

/** #187 — mirrors the server's `TICKET_REMOVAL_REFUND` (src/boundary/ticket.ts). */
export const TICKET_REMOVAL_REFUND = {
  /** Refund first — the WHOLE order when every ticket of it goes — then remove. */
  REFUND: "refund",
  /** Remove without a refund: the host keeps the money (or gave it back another way). */
  KEEP: "keep",
} as const;

export type TicketRemovalRefund = (typeof TICKET_REMOVAL_REFUND)[keyof typeof TICKET_REMOVAL_REFUND];

/** #187 — tickets and/or guests of ONE event to remove (`refund` only when money was paid). */
export interface TicketsRemovalInput {
  eventId: string;
  ticketIds?: string[];
  eventGuestIds?: string[];
  refund?: TicketRemovalRefund | null;
}

export interface TicketsRemovalPlanOrder {
  orderId: string;
  /** Every live ticket of the order goes — ONE refund of what is left of it, fees included. */
  wholeOrder: boolean;
  ticketCount: number;
  refundableAmount: number;
  currencyCode: string | null;
  /** stripe (card) | manual (cash, cheque, terminal — recorded; the host hands the money back). */
  processor: string | null;
  isTest: boolean;
  buyerName: string | null;
  referenceCode: string | null;
}

export interface TicketsRemovalPlan {
  ticketCount: number;
  /** Tickets still holding a place — what goes back on sale. */
  placeCount: number;
  refundableAmount: number;
  currencyCode: string | null;
  needsRefundChoice: boolean;
  /** The caller may choose refund or keep (tickets.manage). */
  mayDecideRefund: boolean;
  blockedReason: string | null;
  blockedMessage: string | null;
  /** Guests holding a removed place who stay on the list — marked not coming. */
  holderGuestCount: number;
  /** Names on tickets a removed buyer bought for other people. */
  otherHolderNames: string[];
  orders: TicketsRemovalPlanOrder[];
}

export interface TicketsRemoved {
  removedTicketIds: string[];
  removedGuestIds: string[];
  refundedOrderIds: string[];
  refundedAmount: number;
  currencyCode: string | null;
  /** Paid orders left for the next call — 0 when done. */
  remainingOrders: number;
}

/** #187 — the Box Office's bulk refund: tickets of ONE event. */
export interface TicketsRefundInput {
  eventId: string;
  ticketIds: string[];
}

export interface TicketsRefunded {
  refundedTicketIds: string[];
  refundedOrderIds: string[];
  refundedAmount: number;
  currencyCode: string | null;
  /** Orders left for the next call — 0 when done. */
  remainingOrders: number;
}

export interface TicketGenerateInput {
  eventId: string;
  ticketTypeId: string;
  quantity: number;
  saleChannel?: string | null;
  /** ROADMAP A4: the date the batch admits to — required on a recurring event. */
  eventTimeSlotId?: string | null;
}

export interface TicketAssignBibsInput {
  eventId: string;
  /** registration | name | random */
  sortOrder: BibSortOrder;
  /** Renumber tickets that already have a bib too (default false = fill blanks). */
  overwrite?: boolean;
}
